import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { CSSSettings } from "hoxton-types";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";
import { EditableType, LegacyEditableTypes } from "hoxton-shared/enums/editables";

import { useEditor } from "@/composables/useEditor";
import { useOverwrite } from "@/composables/useOverwrite";

import getDomValue from "@/utils/getDomValue";
import {
    EditablePhotoshopSettings,
    EditableSettingProperty,
    EditableTranslateMethod,
    type EditablePhotoshopSettingsValues
} from "@/enums/editables";
import { useStore } from "vuex";
import { type RootState } from "@/store/types";
import { EditorAction } from "@/store/modules/editor";

import * as editableUtils from "@/utils/editables";

type CssProperty = NonNullable<keyof CSSSettings>;

type Options = {
    onSettingsChange: (settings: CSSSettings | null) => void;
    // Used for HTML5 banners
    computedStyle?: CSSStyleDeclaration;
    boundingClientRect?: DOMRect;
};

export function useEditableSettings({ onSettingsChange, computedStyle, boundingClientRect }: Options) {
    const store = useStore<RootState>();
    const { editedEditable, fonts, isCampaignPSD, selectedLanguage } = useEditor();
    const { getOverwrite } = useOverwrite();

    const scopeLevel = computed(() =>
        editedEditable.value?.masterTemplateId ? OverwriteScope.EditableGroupTemplate : OverwriteScope.EditableGroup
    );

    const overwrite = computed(() => {
        if (!editedEditable.value) {
            return null;
        }

        return getOverwrite(
            editedEditable.value.editable,
            editedEditable.value.groupsAffectingEditable,
            editedEditable.value.masterTemplateId
        );
    });

    const settings = computed(() => {
        if (!overwrite.value) {
            return null;
        }

        return editableUtils.getEditableSettings(overwrite.value);
    });

    const parentSettings = computed(() =>
        scopeLevel.value === OverwriteScope.EditableGroupTemplate
            ? overwrite.value?.settingsFromScope?.[OverwriteScope.EditableGroup]
            : {}
    );

    // FontStyle
    const fontSize = ref<string | undefined>(settings.value?.fontSize);
    const fontColor = ref<string | undefined>(settings.value?.fontColor);
    const fontFamily = ref<string | undefined>(settings.value?.fontFamily?._id);
    const fontStyle = ref<string | undefined>(settings.value?.fontStyle);
    const letterSpacing = ref<number | undefined>(settings.value?.letterSpacing);
    const lineHeight = ref<number | undefined>(settings.value?.lineHeight);
    const textAlignment = ref<string | undefined>(settings.value?.textAlignment);
    const fontWeight = ref<string | undefined>(settings.value?.fontWeight);

    // AfterEffects font
    const font = ref<string | undefined>(settings.value?.font);

    // ElementPosition
    const width = ref<number | undefined>(settings.value?.width);
    const height = ref<number | undefined>(settings.value?.height);
    const top = ref<number | undefined>(settings.value?.top);
    const left = ref<number | undefined>(settings.value?.left);
    const scale = ref<number | undefined>(settings.value?.scale);
    const translationMethod = ref<"relative" | "transform" | "margin" | "background-position" | undefined>(
        settings.value?.translationMethod
    );
    const preserveAspectRatio = ref(true);
    const aspectRatio = ref(1);
    let unsubscribe: null | (() => void) = null;
    let isRestoringSettings = false;

    onMounted(() => {
        unsubscribe = store.subscribeAction({
            after: action => {
                if (action.type === EditorAction.ClearLocalChanges) {
                    restoreSettings();
                }
            }
        });
    });

    onUnmounted(() => {
        unsubscribe?.();
        unsubscribe = null;
    });

    watch(selectedLanguage, () => {
        restoreSettings();
    });

    watch(translationMethod, () => {
        if (!isRestoringSettings) {
            top.value = undefined;
            left.value = undefined;
        }
    });

    watch(
        () => ({
            fontSize: fontSize.value,
            fontColor: fontColor.value,
            fontFamily: fontFamily.value ? fonts.value[fontFamily.value] : undefined,
            fontStyle: fontStyle.value,
            letterSpacing: letterSpacing.value,
            lineHeight: lineHeight.value,
            textAlignment: textAlignment.value,
            fontWeight: fontWeight.value,
            font: font.value,
            width: width.value,
            height: height.value,
            top: top.value,
            left: left.value,
            scale: scale.value,
            translationMethod: translationMethod.value
        }),
        (newValues, oldValues) => {
            updateCssSettings(newValues, oldValues);
        },
        { deep: false, immediate: true }
    );

    watch([width, height], ([newWidth, newHeight], [oldWidth, oldHeight]) => {
        if (!isCampaignPSD.value || (newWidth === oldWidth && newHeight === oldHeight)) {
            return;
        }

        const editableTypes = [
            LegacyEditableTypes.BackgroundImage,
            EditableType.Image,
            EditableType.Textarea,
            EditableType.Text
        ];

        if (preserveAspectRatio.value && editableTypes.includes(editedEditable.value?.editable.type ?? "")) {
            if (newWidth !== oldWidth) {
                height.value = newWidth ? parseInt((newWidth / aspectRatio.value).toFixed(0)) : undefined;
            }
            if (newHeight !== oldHeight) {
                width.value = newHeight ? parseInt((newHeight * aspectRatio.value).toFixed(0)) : undefined;
            }
        }
    });

    watch(
        editedEditable,
        () => {
            if (!isCampaignPSD.value) {
                return;
            }

            updateAspectRatio();
        },
        { immediate: true }
    );

    watch(preserveAspectRatio, newValue => {
        if (!isCampaignPSD.value || !newValue) {
            return;
        }

        width.value = height.value ? parseInt((height.value * aspectRatio.value).toFixed(0)) : undefined;
    });

    function updateCssSettings(newSettings: CSSSettings, oldSettings: CSSSettings | undefined) {
        const changedEntries = Object.entries(newSettings).filter(([key, value]) => {
            if (key === "fontFamily") {
                const newFontId = (value as CSSSettings["fontFamily"])?._id;
                const oldFontId = oldSettings?.fontFamily?._id;

                const settingsFontId = settings.value?.fontFamily?._id;

                // Check if the font family has changed and is different from both the old settings and the current overwrite settings
                return newFontId !== oldFontId && newFontId !== settingsFontId;
            }

            return (
                value !== oldSettings?.[key as keyof typeof newSettings] &&
                // check for restore changes button to be sure we don't add again the settings to overwrite
                value !== settings.value?.[key as keyof typeof settings.value]
            );
        });

        if (!changedEntries.length) {
            return;
        }

        const merged = {
            ...(settings.value ?? {}),
            ...Object.fromEntries(changedEntries)
        } as Record<string, unknown>;

        if (
            scopeLevel.value === OverwriteScope.EditableGroupTemplate &&
            changedEntries.some(([key]) => key === "fontFamily") &&
            merged.fontFamily === undefined
        ) {
            merged.fontFamily = null;
        }

        const newData = Object.fromEntries(Object.entries(merged).filter(([, value]) => value !== undefined));
        const settingToUpdate = Object.keys(newData).length ? newData : null;

        onSettingsChange(settingToUpdate);
    }

    function updateAspectRatio() {
        const editableType = editedEditable.value?.editable.type;

        if ((editableType === EditableType.Text || editableType === EditableType.Textarea) && !settings.value?.width) {
            aspectRatio.value = 1;
            width.value = 100;
            height.value = 100;

            return;
        }

        if (editableType === EditableType.Image || editableType === LegacyEditableTypes.BackgroundImage) {
            const bounds = editedEditable.value?.editable.layers?.[0]?.value?.bounds;

            if (bounds) {
                const { width, height } = bounds;

                aspectRatio.value = width / height;

                return;
            }
        }

        aspectRatio.value = 1;
    }

    function resetStyles(component: "FontStyle" | "ElementPosition" | "FontStyleAfterEffects") {
        if (component === "FontStyleAfterEffects") {
            fontSize.value = undefined;
            font.value = undefined;

            return;
        }

        if (component === "FontStyle") {
            fontSize.value = undefined;
            fontColor.value = undefined;
            fontFamily.value = undefined;
            fontStyle.value = undefined;
            letterSpacing.value = undefined;
            lineHeight.value = undefined;
            textAlignment.value = undefined;
            fontWeight.value = undefined;

            return;
        }

        // component === "ElementPosition"
        top.value = undefined;
        left.value = undefined;
        width.value = undefined;
        height.value = undefined;
        scale.value = undefined;
        translationMethod.value = undefined;
    }

    function isSuffixVisible(property: CssProperty) {
        if (!computedStyle) {
            return false;
        }

        const domValue = getDomValue(computedStyle, property, boundingClientRect);

        if (domValue === undefined) {
            return true;
        }

        if (Number.isNaN(parseFloat(domValue))) {
            return false;
        }

        return domValue.endsWith("px");
    }

    function getPlaceholderValue(property: CssProperty) {
        const parentSettingsValue = parentSettings.value?.[property];

        if (parentSettingsValue === EditableTranslateMethod.Transform) {
            getPositionPlaceholderValue(property);
        }

        if (property === EditableSettingProperty.TranslationMethod) {
            return parentSettingsValue ?? EditableTranslateMethod.Relative;
        }

        if (typeof parentSettingsValue === "number" && !Number.isNaN(parentSettingsValue)) {
            return parentSettingsValue.toString();
        }

        if (typeof parentSettingsValue === "string") {
            const parsed = parseFloat(parentSettingsValue);

            if (!Number.isNaN(parsed)) {
                return parsed.toString();
            }
        }

        if (computedStyle && boundingClientRect) {
            const domValue = getDomValue(computedStyle, property, boundingClientRect);

            if (domValue !== undefined) {
                const parsedValue = parseFloat(domValue);

                if (!Number.isNaN(parsedValue)) {
                    return parsedValue.toString();
                }

                return domValue;
            }
        }

        return "";
    }

    function getPsdPlaceholderValue(placeholder: EditablePhotoshopSettingsValues) {
        const alignmentPlaceholders: Record<string, string> = {
            left: "Left",
            right: "Right",
            center: "Center",
            justify_left: "Justify Left",
            justify_center: "Justify Center",
            justify_right: "Justify Right",
            justify_all: "Justify All"
        };

        if (placeholder === EditablePhotoshopSettings.textAlignment) {
            const placeholderValue =
                editedEditable.value?.editable.layers?.[0]?.value?.text.paragraphStyles[0]?.[placeholder];

            return placeholderValue ? (alignmentPlaceholders[placeholderValue] ?? placeholderValue) : "";
        }

        return editedEditable.value?.editable.layers?.[0]?.value?.text.characterStyles?.[0]?.[placeholder] ?? "";
    }

    function getPositionPlaceholderValue(property: CssProperty) {
        if (!computedStyle) {
            return;
        }

        const transformStyle = getDomValue(computedStyle, "transform", boundingClientRect);

        if (!transformStyle) {
            return;
        }

        if (transformStyle === "none") {
            return "0";
        }

        if (transformStyle.startsWith("matrix")) {
            const [, , , , translateX, translateY] = transformStyle.split(",");
            if (property === EditableSettingProperty.Left) {
                return translateX;
            }

            if (property === EditableSettingProperty.Top) {
                return translateY?.replace(")", "");
            }
        }
    }

    function restoreSettings() {
        isRestoringSettings = true;

        fontSize.value = settings.value?.fontSize;
        font.value = settings.value?.font;
        fontColor.value = settings.value?.fontColor;
        fontFamily.value = settings.value?.fontFamily?._id;
        fontStyle.value = settings.value?.fontStyle;
        letterSpacing.value = settings.value?.letterSpacing;
        lineHeight.value = settings.value?.lineHeight;
        textAlignment.value = settings.value?.textAlignment;
        fontWeight.value = settings.value?.fontWeight;
        top.value = settings.value?.top;
        left.value = settings.value?.left;
        width.value = settings.value?.width;
        height.value = settings.value?.height;
        scale.value = settings.value?.scale;
        translationMethod.value = settings.value?.translationMethod;

        nextTick(() => {
            isRestoringSettings = false;
        }).catch(() => undefined);
    }

    return {
        isSuffixVisible,
        getPlaceholderValue,
        getPsdPlaceholderValue,
        resetStyles,

        fontSize,
        fontColor,
        fontFamily,
        fontStyle,
        letterSpacing,
        lineHeight,
        textAlignment,
        fontWeight,
        font,

        width,
        height,
        top,
        left,
        scale,
        translationMethod,
        preserveAspectRatio,
        aspectRatio
    };
}
